import React, { FC, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Stack,
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { ListYourVenueLink } from './ListYourVenueLink';
import { VenueSearch } from './VenueSearch';
import { EventsLink } from './EventsLink';
import { FindAVenueButton } from './FindAVenueButton';
import { FavoritesLink } from './FavoritesLink';
import { MarketplaceHomePageLink } from '../MarketplaceHomePageLink';
import { Search } from 'react-feather';
import { AuthMenu } from '../Auth/AuthMenu';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const DesktopToolbarContent = ({
  search = false,
  children,
}: {
  search?: boolean;
  children?: React.ReactNode;
}) => {
  const { guest } = useAuth();

  return (
    <>
      <Stack
        direction="row"
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Stack
          gap="30px"
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{
            xs: 'start',
            md: 'center',
          }}
          margin={{
            xs: '16px 0',
          }}
        >
          <MarketplaceHomePageLink />

          {children}
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          sx={{
            gap: { md: 2, lg: 4 },
          }}
        >
          {search && (
            <Box width="200px">
              <VenueSearch />
            </Box>
          )}
          <ListYourVenueLink />
          <FindAVenueButton />
          {guest && (
            <>
              <FavoritesLink />
              <EventsLink />
            </>
          )}
          <AuthMenu />
        </Stack>
      </Stack>
    </>
  );
};

const MobileToolbarContent = ({
  search = false,
  searchMode,
  handleCloseSearch,
  setMobileMenuOpen,
}: {
  search?: boolean;
  searchMode: boolean;
  handleCloseSearch: () => void;
  setMobileMenuOpen: (open: boolean) => void;
}) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{ width: '100%' }}
    >
      {searchMode ? (
        <>
          <IconButton onClick={handleCloseSearch} aria-label="Close search">
            <CloseIcon />
          </IconButton>
          {search && <VenueSearch />}
        </>
      ) : (
        <>
          <MarketplaceHomePageLink />
          <IconButton
            onClick={() => setMobileMenuOpen(true)}
            sx={{ marginLeft: 'auto' }}
            aria-label="Open mobile navigation menu"
          >
            <MenuIcon sx={{ color: 'black' }} />
          </IconButton>
        </>
      )}
    </Stack>
  );
};

const MobileMenu = ({
  mobileMenuOpen,
  setMobileMenuOpen,
  handleOpenSearch,
  search = false,
}: {
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  handleOpenSearch: () => void;
  search?: boolean;
}) => {
  const { guest, signOut } = useAuth();

  const handleSignOut = () => {
    signOut();
    setMobileMenuOpen(false);
  };

  return (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      aria-label="Mobile navigation menu"
    >
      <Stack spacing={2} sx={{ width: 250, p: 2 }} role="navigation">
        {search && (
          <Button
            onClick={handleOpenSearch}
            startIcon={<Search size={20} />}
            aria-label="Search venues"
          >
            Search venues
          </Button>
        )}
        <ListYourVenueLink />
        <FindAVenueButton />

        {guest ? (
          <>
            <FavoritesLink />
            <EventsLink />
            <Button
              component={RouterLink}
              to="/guests/profile"
              sx={{ color: 'text.primary' }}
            >
              Profile
            </Button>
            <Button variant="outlined" onClick={handleSignOut}>
              Sign Out
            </Button>
          </>
        ) : (
          <AuthMenu />
        )}
      </Stack>
    </Drawer>
  );
};

interface NavigationBarProps {
  search?: boolean;
  children?: React.ReactNode;
}

export const NavigationBar: FC<NavigationBarProps> = ({ search, children }) => {
  const theme = useTheme();
  const mdMedia = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Search mode is used to show the search input in the mobile menu
  const [searchMode, setSearchMode] = useState(false);

  const handleOpenSearch = () => {
    setMobileMenuOpen(false);

    setTimeout(() => {
      setSearchMode(true);
    }, 250);
  };

  const handleCloseSearch = () => {
    setSearchMode(false);
  };

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: '#fff',
        boxShadow: 'none',
        height: '60px',
      }}
    >
      <Toolbar sx={{ position: 'relative', height: '80px' }} role="navigation">
        {!mdMedia ? (
          <DesktopToolbarContent search={search}>
            {children}
          </DesktopToolbarContent>
        ) : (
          <MobileToolbarContent
            search={search}
            searchMode={searchMode}
            handleCloseSearch={handleCloseSearch}
            setMobileMenuOpen={setMobileMenuOpen}
          />
        )}
      </Toolbar>

      <MobileMenu
        search={search}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        handleOpenSearch={handleOpenSearch}
      />
    </AppBar>
  );
};
